import { Component, effect, inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Data, Params } from "@angular/router";
import { SharedContactRepository } from "../../repositories/shared-contact-repository";
import { SharedMessageRepository } from "../../repositories/shared-message-repository";
import { Contact, ContactCreated } from "../../models/contact";
import { MessagePhoto, MessageDraft, MessageDraftSend, MessageCost } from "../../models/message";
import { NavigationService } from "../../helper/navigation-service";
import { PhotoUploadService } from "../../helper/photo-upload.service";
import { PhotoRepository, SuccessfulUpload } from "../../helper/photo-upload";
import { map, Observable, startWith, Subscription, interval, Subject } from "rxjs";
import { PendingCivilianContact, SharedPendingCivilianContactRepository } from "../../repositories/shared-pending-civilian-contact-repository";
import { UploadError } from "../../models/upload-error";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CommonModule, formatNumber } from "@angular/common";
import { LoadingComponent } from "../loading/loading.component";
import { PhotoPreviewComponent } from "../photo-preview/photo-preview";
import { MatOptionModule } from "@angular/material/core";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatInputModule } from "@angular/material/input";
import { MatFormFieldModule } from "@angular/material/form-field";
import { ScreenStore, UserStore } from "../../stores/index";
import { AddContactComponent } from "../add-contact/add-contact.component";
import { SelectContactSearchComponent } from "../select-contact-search/select-contact-search.component";

@Component({
	selector: "jhm-message-draft",
	templateUrl: "./message-draft.component.html",
	styleUrls: ["./message-draft.component.scss"],
	imports: [MatFormFieldModule, MatInputModule, FormsModule, MatAutocompleteModule,
		ReactiveFormsModule, MatOptionModule, PhotoPreviewComponent, LoadingComponent,
		CommonModule, AddContactComponent, AddContactComponent, SelectContactSearchComponent]
})
export class MessageDraftComponent implements OnInit, OnDestroy
{
	public messageDraft: MessageDraft;
	public selectedPage: string = "MESSAGE";
	public contacts: Contact[] = [];
	public availableCredits: number;
	public creditCount: number = 4;
	public photoCount: number = 0;
	public showCreditsInfo: boolean = false;
	public canAddPhotos: boolean;
	public showPhotosModal: boolean = false;
	public uploadingPhoto: boolean = false;
	public photos: MessagePhoto[] = [];
	public emailToAddAsContact: string = "";
	public showInvalidEmailError: boolean = false;
	public isInmate: boolean = false;
	public uploadError: UploadError;
	public selectedContactId: string;
	public selectedContact: Contact;
	public filterString: string;
	public myControl: FormControl = new FormControl("");
	public filteredOptions: Observable<Contact[]>;
	public tempEmailOrBooking: string;
	public loading: boolean = false;
	public screenSize: string;
	public isMobile: boolean = false;
	public showAddContactModal: boolean = false;
	public messageCost: MessageCost = null;

	private emailRegex: RegExp = /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/;
	// This and the code in the constructor are needed to allow addContactWithNewEmailWithContext
	// to have "this" set to the right context.
	public addContactWithNewEmail: (email: string) => void;

	private messageRepository: SharedMessageRepository;
	private activatedRoute: ActivatedRoute;
	private contactRepository: SharedContactRepository;
	private navigationService: NavigationService;
	private photoUploadService: PhotoUploadService;
	private photoRepository: PhotoRepository;
	private autoSaveSubscription: Subscription;
	private civilianRepository: SharedPendingCivilianContactRepository;
	public ngDestroyed$: Subject<any> = new Subject();
	readonly screenStore = inject(ScreenStore);
	readonly userStore = inject(UserStore);

	public constructor(activatedRoute: ActivatedRoute, messageRepository: SharedMessageRepository,
		contactRepository: SharedContactRepository, navigationService: NavigationService,
		photoUploadService: PhotoUploadService, photoRepository: PhotoRepository,
		civilianRepository: SharedPendingCivilianContactRepository)
	{
		this.activatedRoute = activatedRoute;
		this.messageRepository = messageRepository;
		this.contactRepository = contactRepository;
		this.navigationService = navigationService;
		this.photoUploadService = photoUploadService;
		this.photoRepository = photoRepository;
		this.civilianRepository = civilianRepository;
		this.addContactWithNewEmail = (email: string) => this.addContactWithNewEmailWithContext(email);
		this.messageCost = new MessageCost();
		this.messageCost.costPerMessage = 4;
		this.messageCost.costPerPhoto = 4;

		effect(() => {
			this.availableCredits = this.userStore.credits();
			this.isInmate = this.userStore.type() === "INMATE";
		})

		effect(() => {
			this.screenSize = this.screenStore.screenSize();
			if (this.screenSize === "MOBILE") {
				this.isMobile = true;
			}
			else {
				this.isMobile = false;
			}
		})
	}

	public ngOnInit(): void
	{
		// Leaving this here because it may be needed if I share new message and draft message pages
		this.activatedRoute.params.subscribe((params: Params) =>
		{
			if (params.id)
			{
				this.messageRepository.getMessageCost(params.id).subscribe((messageCost: MessageCost) => {
					this.messageCost = messageCost;
					this.creditCount = this.messageCost.totalMessageCost;
				});

				this.messageRepository.getMessageDraft(params.id).subscribe((messageDraft: MessageDraft) => {
					this.setupSavedDraft(messageDraft);
				});
			}
			else
			{
				this.messageRepository.createMessageDraft().subscribe((draftSub: Observable<MessageDraft>) =>
				{
					draftSub.subscribe((mssageDraft: MessageDraft) => {
						this.setupNewDraft(mssageDraft);
					});
				});
			}
		});

		this.activatedRoute.data.subscribe((data: Data) =>
		{
			this.canAddPhotos = data.canAddPhotos;
		});
	}

	public setSelectedContact(newSelectedContact: Contact): void
	{
		let contactString: string = "";
		if (newSelectedContact && this.contacts)
		{
			if (newSelectedContact.isNew)
			{
				this.addContactWithNewEmailWithContext(this.tempEmailOrBooking);
				contactString = newSelectedContact.contactEmailOrBookingNumber;
			}
			else
			{
				this.selectedContactId = newSelectedContact.contactId;
				this.selectedContact = newSelectedContact;
				this.messageDraft.recipientId = this.selectedContact.contactId;

				contactString = this.selectedContact.contactFirstName + " " +
					this.selectedContact.contactLastName;
				if (this.selectedContact.contactType === "CIVILIAN")
				{
					if (this.selectedContact.contactFirstName || this.selectedContact.contactLastName)
					{
						contactString = contactString + " (" + this.selectedContact.contactEmailOrBookingNumber + ")";
					}
					else
					{
						contactString = this.selectedContact.contactEmailOrBookingNumber;
					}
				}
				this.messageRepository.updateMessageDraft(this.messageDraft).subscribe(
					(messageDraft: MessageDraft) =>
					{
						this.calculateCredits();
					}
				);
			}
		}
		this.myControl.setValue(contactString);
	}

	public ngOnDestroy(): void
	{
		this.stopAutoSave();
		this.ngDestroyed$.next(true);
	}

	public canSendMessage(isCollect: boolean): boolean
	{
		return this.messageDraft && this.messageDraft.recipientId !== ""
						&& this.messageDraft.subject !== "" && this.messageDraft.text !== ""
						&& ((this.availableCredits > this.creditCount) || isCollect);
	}

	private startAutoSave(): void
	{
		const autoSaveInterval: Observable<number> = interval(5000);
		this.autoSaveSubscription = autoSaveInterval.subscribe(
			(intervalCount: number) =>
			{
				this.messageRepository.updateMessageDraft(this.messageDraft).subscribe(
					(messageDraft: MessageDraft) =>
					{
					}
				);
				if (intervalCount > 1000)
				{
					this.stopAutoSave();
				}
			}
		);
	}

	private stopAutoSave(): void
	{
		if (this.autoSaveSubscription)
		{
			this.autoSaveSubscription.unsubscribe();
		}
	}

	public addPhotosClicked(): void
	{
		this.showPhotosModal = true;
	}

	public creditsClicked(): void
	{
		this.showCreditsInfo = !this.showCreditsInfo;
	}

	public formatCost(cost: number): string
	{
		return "$" + formatNumber(cost * 0.01, "en-US", "1.2-2");
	}

	public sendClicked(isCollect: boolean): void
	{
		this.loading = true;
		const messageDraftSend: MessageDraftSend = new MessageDraftSend(this.messageDraft, isCollect);
		this.messageRepository.sendMessageDraft(messageDraftSend).subscribe(
			() =>
			{
				this.loading = false;
				this.userStore.fetchUser();
				this.goToPreviousPage();
			}
		);
	}

	public deleteClicked(): void
	{
		this.messageRepository.deleteDraft(this.messageDraft.id).subscribe(() => {
			this.goToPreviousPage();
		});
	}

	public goToPreviousPage()
	{
		this.navigationService.goToPreviousPage("/");
	}

	public calculateCredits()
	{
		if (this.messageDraft && this.messageDraft.id)
		{
			this.messageRepository.getMessageCost(this.messageDraft.id).subscribe((messageCost: MessageCost) => {
				this.messageCost = messageCost;
				this.creditCount = this.messageCost.totalMessageCost;
			});
		}
		else
		{
			this.creditCount = 4;
		}
	}

	public refreshPhotos(photos: MessagePhoto[]): void
	{
		this.photoCount = photos.length;
		this.messageDraft.photos = photos;
		this.calculateCredits();
	}

	public fileSelected(event: any): void
	{
		this.uploadingPhoto = true;
		this.photoUploadService.uploadFiles(event.target.files, "drafts/" + this.messageDraft.id + "/photos")
			.subscribe({
				next: (upload: SuccessfulUpload) =>
				{
					this.refreshPhotos(upload.photos);

					this.uploadingPhoto = false;
					this.showPhotosModal = false;
					this.uploadError = null;
				},
				error: (error: any) =>
				{
					this.uploadError = error.error;
				}
			});
	}

	public formatBytesToMB(number: number): number
	{
		return Math.round(number / 1024000 * 100) / 100;
	}

	public deletePhoto(photo: MessagePhoto): void
	{
		this.photoRepository.deletePhoto(this.messageDraft.id, photo.id)
			.subscribe(() =>
			{
				const updatedPhotos: MessagePhoto[] = this.messageDraft.photos.filter((currentPhoto: MessagePhoto) =>
				{
					return photo.id !== currentPhoto.id;
				});
				this.refreshPhotos(updatedPhotos);
			});
	}

	public addContactWithNewEmailWithContext(email: string)
	{
		const isValidEmail: boolean = !!this.emailRegex.exec(email);
		if (isValidEmail)
		{
			this.showInvalidEmailError = false;
			this.civilianRepository.createPendingCivilianContact(email).subscribe(
				(newPendingContact: PendingCivilianContact) =>
				{
					if (this.contacts.filter((contact: Contact) => contact.contactEmailOrBookingNumber === email).length === 0)
					{
						const newContact: Contact = new Contact();
						newContact.contactId = newPendingContact.id;
						newContact.contactJurisdictionId = newPendingContact.jurisdictionId;
						newContact.contactFirstName = "";
						newContact.contactLastName = "";
						newContact.contactType = "CIVILIAN";
						newContact.contactEmailOrBookingNumber = newPendingContact.email;
						this.contacts = [...this.contacts, newContact];
						this.messageDraft.recipientId = newContact.contactId;
						this.setSelectedContact(newContact);
					}
				}
			);
		}
		else
		{
			this.showInvalidEmailError = true;
		}
	}

	public clearContact()
	{
		this.selectedContact = null;
	}

	public createContactExit(createdContact: ContactCreated): void
	{
		this.showAddContactModal = false;
		if (createdContact)
		{
			this.loading = true;
			this.contactRepository.getContactsPage("").subscribe((contactsPage: Contact[]) => {
				this.setupContacts(contactsPage);
				const recipient: Contact = this.contacts.find((contact: Contact) =>
				{
					return contact.contactId === createdContact.contactUserId;
				});
				this.setSelectedContact(recipient);
				this.loading = false;
			})
		}
	}

	public contactChanged(contact: Contact): void {
		this.messageDraft.recipientId = contact ? contact.contactId : null;
		this.selectedContactId = this.messageDraft.recipientId;
		this.messageRepository.updateMessageDraft(this.messageDraft).subscribe(
			(_messageDraft: MessageDraft) =>
			{
				this.calculateCredits();
			}
		);
	}

	private _filter(value: string): Contact[]
	{
		let contacts: Contact[] = this.contacts;
		if (value)
		{
			const filterValue: string = value.toLowerCase();
			contacts = this.contacts.filter(((contact: Contact) =>
			{
				return (contact.contactFirstName + " " + contact.contactLastName).toLowerCase().includes(filterValue) ||
					contact.contactEmailOrBookingNumber.toLowerCase().includes(filterValue) ||
					(this.selectedContact && contact.contactId === this.selectedContact.contactId);
			}));

			if (this.selectedContact && contacts.length === 1 && contacts[0].contactId === this.selectedContact.contactId)
			{
				contacts = this.contacts;
			}
			else if (contacts.length < 1)
			{
				if (this.isInmate)
				{
					const newContact: Contact = new Contact();
					newContact.contactEmailOrBookingNumber = value;
					newContact.isNew = true;
					this.tempEmailOrBooking = value;
					contacts = [newContact];
				}
				else
				{
					contacts = [];
				}
			}
		}
		return contacts;
	}

	private setupSavedDraft(messageDraft: MessageDraft): void
	{
		this.messageDraft = messageDraft;
		this.refreshPhotos(messageDraft.photos);
		this.startAutoSave();
	}

	private setupNewDraft(messageDraft: MessageDraft): void
	{
		this.messageDraft = messageDraft;
		this.startAutoSave();
	}

	private setupContacts(contactsPage: Contact[]): void
	{
		this.contacts = contactsPage;
		this.filteredOptions = this.myControl.valueChanges.pipe(
			startWith(""),
			map((value: string) =>
			{
				this.clearContact();
				return this._filter(value || "");
			})
		);
	}
}
